/**
 * Flow Comments Panel
 *
 * Comments UI for the Flow Designer's Comments tab. Wires the `/workflows/:id/
 * comments` surface into the same `CommentsTab` presentation issue/card comments
 * use — avatars, relative timestamps, mention-aware editor — resolving each
 * comment's author against the org member directory. Adapted from
 * `features/cards/components/CardCommentsPanel`.
 *
 * Also interleaves the `/workflows/:id/events` audit trail (status changes,
 * node deactivate/reactivate) into the same chronological timeline via
 * `CommentsTab`'s `extraItems`, rendered with `WorkflowEventRow`. The
 * `isLoading`/`isError`/`isFetching` flags passed to `CommentsTab` are the
 * OR of both the comments and events queries, so a failure fetching audit
 * events surfaces the shared error state instead of silently rendering a
 * comments-only timeline that looks complete.
 *
 * @module FlowDesigner/components/FlowCommentsPanel
 */
import React from 'react';
import { Flex } from '@chatsdet/ui';
import type { WorkflowManagementStatus } from '@chatsdet/types';
import { CommentsTab, type TimelineExtraItem } from '../../../components/issues/tabs/CommentsTab';
import { useOrgMembers } from '../../../../hooks/useOrgMembers';
import { useWorkflowComments, useCreateWorkflowComment } from '../hooks/useWorkflowComments';
import { useWorkflowEvents, type TicketInput } from '../hooks/useWorkflowEvents';
import { WorkflowEventRow } from './WorkflowEventRow';
import { WorkflowStatusControl } from './WorkflowStatusControl';

interface FlowCommentsPanelProps {
  workflowId: string;
  className?: string;
  /** When provided, node-reference chips in comment bodies become clickable. */
  onNodeClick?: (nodeId: string) => void;
  /** Manual lifecycle axis, distinct from approval/health. Omitted while the workflow hasn't loaded. */
  workflowManagementStatus?: WorkflowManagementStatus;
  onChangeManagementStatus?: (input: {
    status: WorkflowManagementStatus;
    reason?: string;
    tickets?: TicketInput[];
  }) => void;
  isChangingManagementStatus?: boolean;
}

export function FlowCommentsPanel({
  workflowId,
  className,
  onNodeClick,
  workflowManagementStatus,
  onChangeManagementStatus,
  isChangingManagementStatus,
}: FlowCommentsPanelProps) {
  const commentsQuery = useWorkflowComments(workflowId);
  const createComment = useCreateWorkflowComment(workflowId);
  const eventsQuery = useWorkflowEvents(workflowId);
  const membersQuery = useOrgMembers();
  const members = membersQuery.data?.items ?? [];
  const [commentText, setCommentText] = React.useState('');

  // Single lookup map shared by both the comments and events mappings below,
  // instead of an O(n·m) `members.find(...)` inside each `.map`.
  const membersById = React.useMemo(() => {
    const map = new Map<string, (typeof members)[number]>();
    for (const member of members) {
      map.set(member.userId, member);
    }
    return map;
  }, [members]);

  const comments = React.useMemo(
    () =>
      (commentsQuery.data ?? []).map((c) => {
        const member = membersById.get(c.authorId);
        return {
          id: c.id,
          body: c.body,
          createdAt: c.createdAt,
          author: member
            ? { displayName: member.displayName, primaryEmail: member.primaryEmail }
            : null,
        };
      }),
    [commentsQuery.data, membersById]
  );

  const eventItems = React.useMemo<TimelineExtraItem[]>(
    () =>
      (eventsQuery.data ?? []).map((event) => {
        const member = event.actorKind === 'user' ? membersById.get(event.actorId) : null;
        const actor = member
          ? { displayName: member.displayName, primaryEmail: member.primaryEmail }
          : null;
        return {
          id: event.id,
          createdAt: event.createdAt,
          render: <WorkflowEventRow event={event} actor={actor} onNodeClick={onNodeClick} />,
        };
      }),
    [eventsQuery.data, membersById, onNodeClick]
  );

  return (
    <div className="flex flex-col h-full">
      {/* No "Status" label — the control names its own value ("Active",
          "Paused", …) and the tab it sits in has exactly one control, so the
          label only added a stranded word on the far left with the dropdown
          marooned mid-row. Right-aligned to sit on the panel's gutter. */}
      {workflowManagementStatus && onChangeManagementStatus && (
        <Flex align="center" justify="end" className="px-4 py-3 border-b border-border flex-shrink-0">
          <WorkflowStatusControl
            status={workflowManagementStatus}
            onSubmit={onChangeManagementStatus}
            isSubmitting={isChangingManagementStatus}
          />
        </Flex>
      )}
      <CommentsTab
        comments={comments}
        pagination={undefined}
        page={1}
        isLoading={commentsQuery.isLoading || eventsQuery.isLoading}
        isError={commentsQuery.isError || eventsQuery.isError}
        isFetching={commentsQuery.isFetching || eventsQuery.isFetching}
        commentText={commentText}
        isPosting={createComment.isPending}
        onCommentTextChange={setCommentText}
        onAddComment={() => {
          const trimmed = commentText.trim();
          if (!trimmed) return;
          createComment.mutate({ body: trimmed }, { onSuccess: () => setCommentText('') });
        }}
        onPageChange={() => {}}
        // The sidebar's gutter is px-4 everywhere else (status row above, runs
        // rows, Configure sections); CommentsTab defaults to px-6 for the
        // Issues drawer, which is wider. Override rather than change that
        // default so the drawer keeps its own spacing.
        className={className ?? 'px-4 py-4 space-y-4'}
        mentionableMembers={members}
        onNodeClick={onNodeClick}
        extraItems={eventItems}
      />
    </div>
  );
}
